import { fetchTileSetFile } from "./tiledSource";
import { pickPreview, type TiledDrawingSource } from "./tileMath";

/**
 * How big a preview a consumer needs, as two separate numbers:
 *
 *   - `tierMaxSize` — how sharp the *source* has to be, i.e. which published
 *     tier to fetch (see `pickPreview`).
 *   - `decodeMaxSize` — how many pixels the consumer can actually *display*,
 *     i.e. how large the decoded bitmap may be.
 *
 * They are usually equal, but not for a consumer that shows a zoomed crop
 * rather than the whole page: the mobile mini map needs the 1536 tier so its
 * ~0.3 crop isn't mush, while only ever displaying ~920 of those pixels. Held
 * apart so it can ask for the sharper tier without paying to decode all of it
 * — collapsing them would either pick the far too coarse 512 tier or keep a
 * 1536 bitmap resident all session.
 */
export type PreviewSizing = {
    tierMaxSize: number;
    /** Defaults to `tierMaxSize`, i.e. decode whatever tier was picked at its natural size. */
    decodeMaxSize?: number;
};

/**
 * Fetch and decode the preview tier for a consumer described by `sizing`.
 * Dimensions come from the manifest — no image is decoded just to measure it.
 */
export async function fetchPreviewBitmap(source: TiledDrawingSource, sizing: PreviewSizing): Promise<ImageBitmap> {
    const { tierMaxSize, decodeMaxSize = tierMaxSize } = sizing;
    const preview = pickPreview(source, tierMaxSize);
    if (!preview) {
        throw new Error("Tile set has no previews");
    }

    const response = await fetchTileSetFile(source, preview.path);
    if (!response.ok) {
        throw new Error(`Failed to fetch drawing preview: HTTP ${response.status} for ${preview.path}`);
    }
    const blob = await response.blob();

    const longest = Math.max(preview.width, preview.height);
    if (longest <= decodeMaxSize) {
        return createImageBitmap(blob);
    }
    const scale = decodeMaxSize / longest;
    return createImageBitmap(blob, {
        resizeWidth: Math.max(1, Math.round(preview.width * scale)),
        resizeHeight: Math.max(1, Math.round(preview.height * scale)),
        resizeQuality: "high",
    });
}
